import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, X, Pencil, Trash2, ExternalLink, Music2, Search, ClipboardPaste } from "lucide-react";
import { apiFetch, apiPost, apiPatch, apiDelete } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles, getSimulatedIsMusician } from "./roleSimulator";
import { cifraUrlComTom } from "./musicKeys";

// Catalogo de repertorio da equipe de louvor (modulo pedido pelo Rodrigo,
// 10/2026). Comecou como ideia de "multitrack" com stems separados por
// instrumento, mas isso exigiria arquivos de audio que o YouTube nao
// fornece -- virou um catalogo de musicas com link de referencia (YouTube,
// normalmente) pra quem toca estudar a propria parte antes do ensaio.
// So ADMIN/MUSICO gerenciam e consultam o catalogo -- pedido do Rodrigo,
// 10/2026 ("editor de conteudo nao, so musico mesmo"). Ver GET/POST/PATCH
// /songs no backend.

const ADMIN_PATH = "/admin";

// "Hoje" sempre no fuso da igreja (Sao Paulo), nunca no fuso de quem esta
// usando o admin nem em UTC -- mesmo padrao do fmtDateInput em
// AdminAgentRelatorio.jsx. Usado so pra travar o campo "ultima vez tocada"
// (nao pode ser no futuro).
function todayInSaoPaulo() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

// 40x40 (nao 32x32) -- revisao de UX 10/2026: 32px ficava abaixo do touch
// target minimo recomendado (44x44 iOS / 48x48 Android); numa lista rolando
// rapido no celular, editar/excluir colados e pequenos geram mistap fácil
// bem do lado de uma acao destrutiva.
const iconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: 9,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "rgba(255,255,255,0.03)",
  color: "#c7ced9",
  cursor: "pointer"
};

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(4,8,18,0.72)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 50
};

const modalCardStyle = {
  width: "100%",
  // 460 -> 640 (17/10/2026, feedback do Drigo: "muito estreito") -- o
  // formulario tem campo de URL longa (cifra/referencia) e a busca
  // assistida (iframe do Cifra Club) embutida dentro do mesmo modal;
  // 460px truncava a URL cedo demais e deixava tudo espremido.
  maxWidth: 640,
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.32)",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
  position: "relative",
  maxHeight: "86vh",
  overflowY: "auto"
};

const modalCloseButtonStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "rgba(255,255,255,0.06)",
  borderRadius: 8,
  color: "#9fb0cc",
  cursor: "pointer"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "rgba(255,255,255,0.04)",
  color: "#e5e7eb",
  fontSize: 14,
  fontFamily: "inherit"
};

const fieldLabelStyle = {
  display: "block",
  margin: "0 0 6px",
  fontSize: 12,
  fontWeight: 800,
  color: "#9fb0cc"
};

// Link de busca assistida (YouTube / Cifra Club) ao lado do campo de link
// correspondente no formulario -- abre a busca pronta numa aba nova pra
// quem cadastra escolher o resultado certo com os proprios olhos e colar o
// link de volta aqui. Nao preenche nada sozinho: tentar casar titulo com
// video/cifra automaticamente arrisca gravar o errado sem confirmacao
// humana (mesmo problema ja visto com os links do grupo de louvor).
const assistSearchLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 11,
  fontWeight: 700,
  color: "#7DD3FC",
  textDecoration: "none"
};

const primaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(125,211,252,0.4)",
  background: "rgba(14,165,233,0.18)",
  color: "#BAE6FD",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

const secondaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "transparent",
  color: "#c7ced9",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer"
};

const dangerButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(248,113,113,0.4)",
  background: "rgba(248,113,113,0.14)",
  color: "#FCA5A5",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

const EMPTY_FORM = { title: "", artist: "", key: "", bpm: "", referenceUrl: "", lyricsUrl: "", lastPlayedAt: "", notes: "", isActive: true };

// "2026-08-02T00:00:00.000Z" (ou so-data) -> "02/08/2026", pro badge "Tocada
// em" no card. Mesma logica de input[type=date]: so usa os 10 primeiros
// caracteres (YYYY-MM-DD), sem conversao de fuso (a data e so-dia, sem
// hora que importe).
function formatLastPlayed(value) {
  if (!value) return null;
  const iso = String(value).slice(0, 10);
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return null;
  return `${d}/${m}/${y}`;
}

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

export default function AdminRepertorio() {
  const [status, setStatus] = useState("checking");
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Filtros rapidos pra achar quem ainda falta completar o cadastro --
  // pedido do Drigo depois da recuperacao do repertorio (28/10/2026): boa
  // parte das musicas recuperadas ficou sem link nenhum, e sem isso era
  // dificil achar quem precisava de atencao numa lista de 38+ musicas.
  const [semYoutube, setSemYoutube] = useState(false);
  const [semCifra, setSemCifra] = useState(false);
  const [feedback, setFeedback] = useState("");

  // Modal "Nova musica" / "Editar musica" -- mesmo formulario pros dois,
  // editingSong null = criando.
  const [formOpen, setFormOpen] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Busca assistida inline (17/10/2026, pedido do Drigo: trazer resultado
  // direto na tela em vez de so abrir aba nova). YouTube usa a API oficial
  // (ver youtubeResults); Cifra Club nao tem API publica, entao vira um
  // iframe com a busca deles dentro do proprio formulario (com link de
  // "abrir em aba nova" sempre visivel do lado, caso o site bloqueie
  // iframe).
  const [youtubeResults, setYoutubeResults] = useState(null);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [youtubeError, setYoutubeError] = useState("");
  const [cifraIframeOpen, setCifraIframeOpen] = useState(false);
  const [pasteFeedback, setPasteFeedback] = useState("");

  // Confirmacao de exclusao -- nao usa window.confirm (sem estilo, trava a pagina).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const roles = getEffectiveRoles(me);
        // Capacidade de musico mora na Person (isMusician), nao em
        // User.roles -- mesmo motivo/correcao de AdminEscalas.jsx
        // (20/10/2026, caso do Gabriel).
        const realIsAdminUser = Boolean(me?.roles?.includes("ADMIN"));
        const isMusicianByPerson = getSimulatedIsMusician(realIsAdminUser, me?.person?.isMusician);
        if ((roles.includes("ADMIN") || roles.includes("MUSICO") || isMusicianByPerson) && !me.passwordMustChange) {
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  const loadSongs = useCallback(async () => {
    setLoading(true);
    setFeedback("");
    try {
      const data = await apiFetch("/songs/all", { method: "GET" });
      setSongs(Array.isArray(data) ? data : []);
    } catch (error) {
      setFeedback("Não foi possível carregar o repertório agora.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "ready") loadSongs();
  }, [status, loadSongs]);

  const resetAssistSearch = () => {
    setYoutubeResults(null);
    setYoutubeLoading(false);
    setYoutubeError("");
    setCifraIframeOpen(false);
    setPasteFeedback("");
  };

  const openCreate = () => {
    setEditingSong(null);
    setForm(EMPTY_FORM);
    setFormError("");
    resetAssistSearch();
    setFormOpen(true);
  };

  const openEdit = (song) => {
    setEditingSong(song);
    setForm({
      title: song.title || "",
      artist: song.artist || "",
      key: song.key || "",
      bpm: song.bpm != null ? String(song.bpm) : "",
      referenceUrl: song.referenceUrl || "",
      lyricsUrl: song.lyricsUrl || "",
      lastPlayedAt: song.lastPlayedAt ? String(song.lastPlayedAt).slice(0, 10) : "",
      notes: song.notes || "",
      isActive: Boolean(song.isActive)
    });
    setFormError("");
    resetAssistSearch();
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditingSong(null);
  };

  // Cola o conteudo da area de transferencia direto no campo de cifra --
  // nao resolve o problema de fundo (iframe de outro dominio nao deixa a
  // gente ler pra onde a pessoa navegou la dentro, ver comentario acima),
  // mas poupa o passo de voltar pra aba, clicar no campo e colar na mao
  // depois de copiar o link na aba nova.
  const pasteCifraFromClipboard = async () => {
    setPasteFeedback("");
    if (!navigator.clipboard || !navigator.clipboard.readText) {
      setPasteFeedback("Esse navegador não deixa colar automaticamente aqui — cole manualmente (Ctrl/Cmd+V) no campo.");
      return;
    }
    try {
      const text = (await navigator.clipboard.readText()).trim();
      if (!text) {
        setPasteFeedback("A área de transferência está vazia — copie o link na aba nova primeiro.");
        return;
      }
      updateForm("lyricsUrl", text);
      setPasteFeedback("Colado!");
    } catch (error) {
      setPasteFeedback("Não consegui acessar a área de transferência — autorize o acesso ou cole manualmente (Ctrl/Cmd+V).");
    }
  };

  const runYoutubeSearch = async () => {
    const query = `${form.title} ${form.artist}`.trim();
    if (!query) return;
    setYoutubeLoading(true);
    setYoutubeError("");
    try {
      const data = await apiFetch(`/songs/youtube-search?q=${encodeURIComponent(query)}`, { method: "GET" });
      const results = Array.isArray(data?.results) ? data.results : [];
      setYoutubeResults(results);
      // Traz o primeiro resultado ja preenchido no campo -- pedido do
      // Drigo (17/10/2026): "traz o primeiro e se quiser a pessoa desce na
      // lista pra escolher o proximo". Continua sendo so um ponto de
      // partida: a pessoa confere e troca se nao for o video certo.
      if (results[0]) {
        setForm((prev) => ({ ...prev, referenceUrl: results[0].url }));
      }
    } catch (error) {
      setYoutubeResults(null);
      setYoutubeError(error?.message || "Não foi possível buscar no YouTube agora.");
    } finally {
      setYoutubeLoading(false);
    }
  };

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const submitForm = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setFormError("Dê um título pra música.");
      return;
    }
    if (form.lastPlayedAt && form.lastPlayedAt > todayInSaoPaulo()) {
      setFormError("\"Última vez tocada\" não pode ser uma data futura -- isso é a data em que a música JÁ tocou, não a data em que ela está escalada pra tocar.");
      return;
    }
    setSaving(true);
    setFormError("");
    const body = {
      title: form.title.trim(),
      artist: form.artist.trim() || null,
      key: form.key.trim() || null,
      bpm: form.bpm.trim() ? Number(form.bpm.trim()) : null,
      referenceUrl: form.referenceUrl.trim() || null,
      lyricsUrl: form.lyricsUrl.trim() || null,
      lastPlayedAt: form.lastPlayedAt || null,
      notes: form.notes.trim() || null,
      isActive: Boolean(form.isActive)
    };
    try {
      if (editingSong) {
        await apiPatch(`/songs/${editingSong.id}`, { body });
      } else {
        await apiPost("/songs", { body });
      }
      // Recarrega a lista inteira em vez de so trocar o item em memoria --
      // trocar em memoria mantinha a musica na mesma posicao do array,
      // entao editar o "Ultima vez tocada" (ou criar uma musica nova) nao
      // reordenava a tela, so depois de um F5 (bug reportado pelo Drigo em
      // 17/10/2026). Recarregar busca a ordem certa direto do backend
      // (lastPlayedAt desc, nulls last, titulo asc).
      await loadSongs();
      setFormOpen(false);
      setEditingSong(null);
    } catch (error) {
      setFormError(error?.message || "Não foi possível salvar a música.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/songs/${deleteTarget.id}`);
      setSongs((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error) {
      setFeedback(error?.message || "Não foi possível excluir a música.");
    } finally {
      setDeleting(false);
    }
  };

  if (status === "checking") return <CenteredMessage>Carregando…</CenteredMessage>;
  if (status === "forbidden") return <CenteredMessage>Você não tem acesso a esta área.</CenteredMessage>;

  const normalizedSearch = search.trim().toLowerCase();
  const filteredSongs = songs
    .filter((song) =>
      normalizedSearch
        ? `${song.title} ${song.artist || ""}`.toLowerCase().includes(normalizedSearch)
        : true
    )
    .filter((song) => (semYoutube ? !song.referenceUrl : true))
    .filter((song) => (semCifra ? !song.lyricsUrl : true));

  // Usado tanto no iframe de busca do Cifra Club quanto no link de
  // fallback "abrir em aba nova" (ver bloco "Link de cifra/letra" mais
  // abaixo).
  const cifraSearchUrl = `https://www.cifraclub.com.br/?q=${encodeURIComponent(`${form.title} ${form.artist}`.trim())}`;

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
          <div>
            <h1 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 900, display: "flex", alignItems: "center", gap: 8 }}>
              <Music2 size={20} /> Repertório
            </h1>
            <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>
              Catálogo de músicas pra equipe de louvor estudar antes do ensaio — cada uma com um link de referência (normalmente um vídeo do YouTube).
            </p>
          </div>
          <button type="button" style={primaryButtonStyle} onClick={openCreate}>
            <Plus size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
            Nova música
          </button>
        </div>

        <div style={{ ...cardStyle, marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <Search size={16} color="#9fb0cc" />
          <input
            style={{ ...inputStyle, border: "none", background: "transparent", padding: "6px 0" }}
            placeholder="Buscar por título ou artista…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          <button
            type="button"
            onClick={() => setSemYoutube((prev) => !prev)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              border: semYoutube ? "1px solid #7DD3FC" : "1px solid rgba(148,163,184,0.28)",
              background: semYoutube ? "rgba(125,211,252,0.14)" : "rgba(255,255,255,0.03)",
              color: semYoutube ? "#7DD3FC" : "#9fb0cc"
            }}
          >
            Sem link do YouTube
          </button>
          <button
            type="button"
            onClick={() => setSemCifra((prev) => !prev)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              border: semCifra ? "1px solid #7DD3FC" : "1px solid rgba(148,163,184,0.28)",
              background: semCifra ? "rgba(125,211,252,0.14)" : "rgba(255,255,255,0.03)",
              color: semCifra ? "#7DD3FC" : "#9fb0cc"
            }}
          >
            Sem link de cifra
          </button>
        </div>

        {feedback && (
          <div style={{ ...cardStyle, marginBottom: 16, color: "#FCA5A5", fontSize: 13 }}>{feedback}</div>
        )}

        {loading && <p style={{ color: "#9fb0cc", fontSize: 13 }}>Carregando repertório…</p>}

        {!loading && filteredSongs.length === 0 && (
          <div style={{ ...cardStyle, textAlign: "center", color: "#9fb0cc", fontSize: 13 }}>
            {songs.length === 0 ? "Nenhuma música no repertório ainda." : "Nenhuma música encontrada pra essa busca."}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filteredSongs.map((song) => (
            <div key={song.id} style={{ ...cardStyle, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, opacity: song.isActive ? 1 : 0.55 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <strong style={{ fontSize: 15 }}>{song.title}</strong>
                  {!song.isActive && (
                    <span style={{ fontSize: 10, fontWeight: 800, color: "#9fb0cc", border: "1px solid rgba(148,163,184,0.32)", borderRadius: 999, padding: "2px 8px" }}>
                      Inativa
                    </span>
                  )}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "#9fb0cc" }}>
                  {[song.artist, song.key ? `Tom ${song.key}` : null, song.bpm ? `${song.bpm} bpm` : null]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </div>
                {formatLastPlayed(song.lastPlayedAt) && (
                  <div style={{ marginTop: 6, fontSize: 11, color: "#6b7a99" }}>
                    Tocada em {formatLastPlayed(song.lastPlayedAt)}
                  </div>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                  {song.referenceUrl && (
                    <a href={song.referenceUrl} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, fontSize: 12, fontWeight: 700, color: "#7DD3FC", textDecoration: "none" }}>
                      <ExternalLink size={12} /> Ouvir referência
                    </a>
                  )}
                  {song.lyricsUrl && (
                    <a href={cifraUrlComTom(song.lyricsUrl, song.key)} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, fontSize: 12, fontWeight: 700, color: "#7DD3FC", textDecoration: "none" }}>
                      <ExternalLink size={12} /> Ver cifra{song.key ? ` (tom ${song.key})` : ""}
                    </a>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, flexShrink: 0 }}>
                <button type="button" style={iconButtonStyle} onClick={() => openEdit(song)} title="Editar">
                  <Pencil size={14} />
                </button>
                <button type="button" style={iconButtonStyle} onClick={() => setDeleteTarget(song)} title="Excluir">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {formOpen && (
        <div style={modalBackdropStyle} onClick={closeForm}>
          <form style={modalCardStyle} onClick={(event) => event.stopPropagation()} onSubmit={submitForm}>
            <button type="button" style={modalCloseButtonStyle} onClick={closeForm} aria-label="Fechar">
              <X size={14} />
            </button>
            <h2 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 900 }}>
              {editingSong ? "Editar música" : "Nova música"}
            </h2>

            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Título *</label>
              <input style={inputStyle} value={form.title} onChange={(event) => updateForm("title", event.target.value)} required />
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={fieldLabelStyle}>Artista</label>
                <input style={inputStyle} value={form.artist} onChange={(event) => updateForm("artist", event.target.value)} />
              </div>
              <div style={{ width: 90 }}>
                <label style={fieldLabelStyle}>Tom</label>
                <input style={inputStyle} value={form.key} onChange={(event) => updateForm("key", event.target.value)} placeholder="Ex.: G" />
              </div>
              <div style={{ width: 90 }}>
                <label style={fieldLabelStyle}>BPM</label>
                <input style={inputStyle} value={form.bpm} onChange={(event) => updateForm("bpm", event.target.value)} inputMode="numeric" />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Última vez tocada</label>
              <input
                type="date"
                style={{ ...inputStyle, colorScheme: "dark" }}
                value={form.lastPlayedAt}
                max={todayInSaoPaulo()}
                onChange={(event) => updateForm("lastPlayedAt", event.target.value)}
              />
              <p style={{ margin: "6px 0 0", fontSize: 11, color: "#6b7a99" }}>
                Quando a música já tocou (não quando está escalada pra tocar) -- usado pra ordenar o catálogo (mais
                tocadas recentemente aparecem primeiro). Não aceita data futura.
              </p>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <label style={fieldLabelStyle}>Link de referência (YouTube)</label>
                {form.title.trim() && (
                  <button
                    type="button"
                    onClick={runYoutubeSearch}
                    disabled={youtubeLoading}
                    style={{ ...assistSearchLinkStyle, background: "none", border: "none", padding: 0, cursor: youtubeLoading ? "default" : "pointer", opacity: youtubeLoading ? 0.6 : 1 }}
                  >
                    <Search size={11} /> {youtubeLoading ? "Buscando…" : "Buscar no YouTube"}
                  </button>
                )}
              </div>
              <input style={inputStyle} value={form.referenceUrl} onChange={(event) => updateForm("referenceUrl", event.target.value)} placeholder="https://..." />

              {youtubeError && (
                <p style={{ margin: "8px 0 0", fontSize: 11, color: "#fca5a5" }}>{youtubeError}</p>
              )}

              {Array.isArray(youtubeResults) && youtubeResults.length === 0 && (
                <p style={{ margin: "8px 0 0", fontSize: 11, color: "#6b7a99" }}>Nenhum resultado encontrado.</p>
              )}

              {Array.isArray(youtubeResults) && youtubeResults.length > 0 && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto", paddingRight: 2 }}>
                  {youtubeResults.map((video) => {
                    const selected = form.referenceUrl === video.url;
                    return (
                      <button
                        key={video.videoId}
                        type="button"
                        onClick={() => updateForm("referenceUrl", video.url)}
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                          textAlign: "left",
                          padding: 8,
                          borderRadius: 6,
                          border: selected ? "1px solid #7DD3FC" : "1px solid rgba(255,255,255,0.1)",
                          background: selected ? "rgba(125,211,252,0.12)" : "rgba(255,255,255,0.03)",
                          cursor: "pointer"
                        }}
                      >
                        {video.thumbnailUrl && (
                          <img src={video.thumbnailUrl} alt="" style={{ width: 64, height: 48, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{ margin: 0, fontSize: 12, fontWeight: 800, color: "#e5e7eb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{video.title}</p>
                          <p style={{ margin: "2px 0 0", fontSize: 11, color: "#7f8da3" }}>{video.channelTitle}</p>
                        </div>
                        {selected && <span style={{ fontSize: 10, color: "#7DD3FC", fontWeight: 900, flexShrink: 0 }}>Selecionado</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <label style={fieldLabelStyle}>Link de cifra/letra</label>
                {form.title.trim() && (
                  <button
                    type="button"
                    onClick={() => setCifraIframeOpen((open) => !open)}
                    style={{ ...assistSearchLinkStyle, background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  >
                    <Search size={11} /> {cifraIframeOpen ? "Fechar busca" : "Buscar no Cifra Club"}
                  </button>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input style={{ ...inputStyle, flex: 1 }} value={form.lyricsUrl} onChange={(event) => updateForm("lyricsUrl", event.target.value)} placeholder="https://..." />
                <button
                  type="button"
                  onClick={pasteCifraFromClipboard}
                  title="Colar da área de transferência"
                  style={{ ...iconButtonStyle, flexShrink: 0 }}
                >
                  <ClipboardPaste size={14} />
                </button>
              </div>
              {pasteFeedback && (
                <p style={{ margin: "4px 0 0", fontSize: 11, color: pasteFeedback === "Colado!" ? "#7DD3FC" : "#FCA5A5" }}>{pasteFeedback}</p>
              )}

              {cifraIframeOpen && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ padding: "8px 10px", borderRadius: 6, background: "rgba(125,211,252,0.1)", border: "1px solid rgba(125,211,252,0.3)", marginBottom: 8 }}>
                    <p style={{ margin: 0, fontSize: 11.5, color: "#dbeafe", lineHeight: 1.45 }}>
                      Ache a música aqui embaixo, clique em <strong>"Abrir em aba nova ↗"</strong>, copie o link de lá e cole acima (ou use o botão <ClipboardPaste size={10} style={{ verticalAlign: "-1px" }} />). Dentro do quadro não dá pra copiar direto (site de outro domínio, sem barra de endereço).
                      {form.key.trim() && <> Já abre no tom <strong>{form.key.trim()}</strong> automaticamente.</>}
                    </p>
                  </div>
                  <div style={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "rgba(255,255,255,0.05)" }}>
                      <span style={{ fontSize: 10, color: "#7f8da3", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Cifra Club (só pra conferir)</span>
                      <a href={cifraSearchUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#7DD3FC", fontWeight: 800, textDecoration: "none" }}>
                        Abrir em aba nova ↗
                      </a>
                    </div>
                    <iframe
                      title="Busca no Cifra Club"
                      src={cifraSearchUrl}
                      style={{ width: "100%", height: 480, border: "none", background: "#fff" }}
                    />
                  </div>
                </div>
              )}
            </div>

            <p style={{ margin: "-6px 0 12px", fontSize: 11, color: "#6b7a99" }}>
              A busca do YouTube já traz o primeiro resultado preenchido — confira e troque na lista se não for o vídeo certo. Pra cifra, abra em aba nova, copie o link da cifra certa e cole no campo (o quadro embutido é só pra olhar).
            </p>

            <div style={{ marginBottom: 12 }}>
              <label style={fieldLabelStyle}>Observações</label>
              <textarea
                style={{ ...inputStyle, minHeight: 72, resize: "vertical" }}
                value={form.notes}
                onChange={(event) => updateForm("notes", event.target.value)}
              />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, fontSize: 13, color: "#c7ced9" }}>
              <input type="checkbox" checked={form.isActive} onChange={(event) => updateForm("isActive", event.target.checked)} />
              Ativa no repertório (visível pra equipe de louvor)
            </label>

            {formError && <p style={{ color: "#FCA5A5", fontSize: 13, marginBottom: 12 }}>{formError}</p>}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" style={secondaryButtonStyle} onClick={closeForm} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" style={primaryButtonStyle} disabled={saving}>
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </form>
        </div>
      )}

      {deleteTarget && (
        <div style={modalBackdropStyle} onClick={() => !deleting && setDeleteTarget(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 380 }} onClick={(event) => event.stopPropagation()}>
            <h2 style={{ margin: "0 0 10px", fontSize: 16, fontWeight: 900 }}>Excluir música?</h2>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: "#c7ced9" }}>
              Vai remover <strong>{deleteTarget.title}</strong> do repertório. Essa ação não pode ser desfeita.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" style={secondaryButtonStyle} onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancelar
              </button>
              <button type="button" style={dangerButtonStyle} onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Excluindo…" : "Excluir"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
